import { create } from 'zustand';
import {
  ActionEntityType,
  CreateTableViewRequest,
  TableViewResponse,
  getTableViewsForADataType,
  createTableView,
  updateTableViews,
  deleteTableView,
} from '@pmp/api';

type TableViewsStore = {
  tableViews: Partial<Record<ActionEntityType, TableViewResponse[]>>;
  isLoadingTableViews: boolean;
  getTableViews: (entityType: ActionEntityType) => Promise<TableViewResponse[]>;
  saveTableView: (entityType: ActionEntityType, tableView: Omit<TableViewResponse, 'tableColumnSizes'>) => Promise<string>;
  deleteTableView: (entityType: ActionEntityType, id: string) => Promise<void>;
};

export const useTableViewsStore = create<TableViewsStore>((set, get) => {
  return {
    tableViews: {},
    isLoadingTableViews: false,

    getTableViews: async (entityType: ActionEntityType) => {
      const { tableViews } = get();

      if (tableViews[entityType]) {
        return tableViews[entityType];
      }

      set({ isLoadingTableViews: true });

      try {
        const { data } = await getTableViewsForADataType({ path: { entityType } });
        set({
          tableViews: {
            ...tableViews,
            [entityType]: data,
          },
          isLoadingTableViews: false,
        });
        return data;
      } catch (error) {
        set({ isLoadingTableViews: false });
        throw new Error(error instanceof Error ? error.message : String(error));
      }
    },

    saveTableView: async (entityType: ActionEntityType, tableView: Omit<TableViewResponse, 'tableColumnSizes'>) => {
      const payload: CreateTableViewRequest = {
        name: tableView.name,
        entityType,
        selected: tableView.selected,
        order: tableView.order,
        fixedTableColumns: tableView.fixedTableColumns,
        tableColumnSizes: tableView.tableColumnSizes,
      };

      if (tableView.id === 'new') {
        const response = await createTableView({ body: payload });
        const { id } = response.data as TableViewResponse;
        const updatedViews = [...(get().tableViews[entityType] || []), response.data];
        set({
          tableViews: {
            ...get().tableViews,
            [entityType]: updatedViews,
          },
        });

        return id;
      } else {
        const { data }: { data: TableViewResponse } = await updateTableViews({
          path: { viewId: tableView.id },
          body: payload,
          query: { version: tableView.versionNumber },
        });
        const updatedViews =
          get().tableViews[entityType]?.map(view =>
            view.id === tableView.id ? { ...view, ...payload, versionNumber: data.versionNumber } : view,
          ) || [];
        set({
          tableViews: {
            ...get().tableViews,
            [entityType]: updatedViews,
          },
        });

        return tableView.id;
      }
    },

    deleteTableView: async (entityType: ActionEntityType, id: string) => {
      await deleteTableView({ path: { viewId: id } });
      const updatedViews = get().tableViews[entityType]?.filter(view => view.id !== id) || [];
      set({
        tableViews: {
          ...get().tableViews,
          [entityType]: updatedViews,
        },
      });
    },
  };
});

// TODO: Check if we can use use ActionEntityType as tableViewId to avoid conversion
export const tableViewByEntity: Partial<Record<ActionEntityType, string>> = {
  [ActionEntityType.PRODUCTS]: 'products',
  [ActionEntityType.BASE_RATES]: 'rates',
  [ActionEntityType.ERC_PROFILES]: 'ercProfiles',
  [ActionEntityType.FEES]: 'fees',
  [ActionEntityType.FEE_PROFILES]: 'feeProfiles',
};
